import type {
  ClientProposalTemplateJSON,
  UpdateClientProposalTemplateRequest,
} from "work/client-proposal-templates/_schema/client-proposal-template-schema";

/**
 * The defaults as the screen holds them while somebody is editing.
 *
 * `copyFromProposalTemplateId` is the saved template picked since the last save, or null when none
 * has been: the copy the defaults already hold is the server's, not something the screen edits, so
 * there is nothing to seed it from. Picking one is a request to copy it on save.
 */
export type ClientProposalTemplateFormState = {
  copyFromProposalTemplateId: string | null;
  autoCreateEnabled: boolean;
};

export function toFormState(
  configuration: ClientProposalTemplateJSON | undefined,
): ClientProposalTemplateFormState {
  return {
    copyFromProposalTemplateId: null,
    autoCreateEnabled: configuration?.autoCreateEnabled ?? false,
  };
}

export function toUpdateRequest(
  state: ClientProposalTemplateFormState,
): UpdateClientProposalTemplateRequest {
  return {
    copyFromProposalTemplateId: state.copyFromProposalTemplateId,
    autoCreateEnabled: state.autoCreateEnabled,
  };
}

/**
 * Whether the screen is holding an edit that has not been sent.
 *
 * A picked template is always a change, even the one the current copy came from: picking it again
 * is how a reader takes up the edits made to it since. The switch is compared against the record
 * the API last returned rather than tracked as a dirty flag, so flicking it and back reads as
 * unchanged.
 */
export function hasUnsavedChanges(
  state: ClientProposalTemplateFormState,
  configuration: ClientProposalTemplateJSON | undefined,
): boolean {
  return (
    state.copyFromProposalTemplateId !== null ||
    state.autoCreateEnabled !== toFormState(configuration).autoCreateEnabled
  );
}
